import * as React from 'react';
import { useEffect, useState, useRef, RefObject } from 'react';
import { useMediaQuery } from 'react-responsive';

import { Device, ButtonTheme, Color, DialogTheme, IconName } from '../../enums';
import { SmallDialogSettings } from '../../interfaces';
import { Overlay } from '../../theme';
import { UseState } from '../../types';
import { Icon } from '../icon/icon.component';

import {
  ButtonsSection,
  CloseButton,
  Container,
  Content,
  ContentSection,
  Header,
  SmallDialogWrapper,
  StyledButton
} from './smallDialog.styled';
import { DialogRefType, SmallDialogProps } from './smallDialog.types';

export const SmallDialog: React.FC<SmallDialogProps> = (props: SmallDialogProps): JSX.Element => {
  const { settings, children, className }: SmallDialogProps = props;
  const {
    isActive = false,
    header,
    content,
    acceptButton,
    cancelButton,
    closeButtonAction,
    theme = DialogTheme.Small,
    withCloseButton = true,
  }: SmallDialogSettings = settings;
  const [isVisible, setIsVisible]: UseState<boolean> = useState<boolean>(isActive);
  const ref: RefObject<DialogRefType> = useRef(null);
  const isTouchDevice: boolean = useMediaQuery({ query: Device.TouchDevice });

  useEffect(
    (): void => {
      setIsVisible(isActive);
      const dialogRef: DialogRefType | null = ref.current;

      if (isTouchDevice) {
        if (isActive) {
          if (dialogRef) {
            if (dialogRef.requestFullscreen) {
              void dialogRef.requestFullscreen();
            } else if (dialogRef.webkitRequestFullscreen) {
              void dialogRef.webkitRequestFullscreen();
            } else if (dialogRef.mozRequestFullScreen) {
              void dialogRef.mozRequestFullScreen();
            }
          }
        } else if (document.exitFullscreen) {
          void document.exitFullscreen();
        }
      }
    },
    [isActive]
  );

  const cancelAction: () => void = (): void => {
    if (cancelButton?.action) {
      cancelButton.action();

      if (isTouchDevice) {
        void document.exitFullscreen();
      }
    }
  };

  const acceptAction: () => void = (): void => {
    if (acceptButton?.action) {
      acceptButton.action();
    }
  };

  const closeAction: () => void = (): void => {
    if (closeButtonAction) {
      closeButtonAction();
    } else if (cancelButton?.action) {
      cancelButton.action();
    }
    
    if (isTouchDevice) {
      void document.exitFullscreen();
    }
  };

  return (
    <SmallDialogWrapper>
      <Container className={className} $isVisible={isVisible} $theme={theme} ref={ref}>
        <ContentSection>
          <Header>{header}</Header>
          {content ? <Content text={content} /> : children}
          {withCloseButton && (
            <CloseButton onClick={closeAction}>
              <Icon name={IconName.Close} size={12} color={Color.Gray500} />
            </CloseButton>
          )}
        </ContentSection>
        {(cancelButton?.label || acceptButton?.label) && (
          <ButtonsSection>
            {cancelButton?.label && (
              <StyledButton
                label={cancelButton.label}
                buttonTheme={ButtonTheme.SecondarySolidSmall}
                onClick={cancelAction}
                isLoading={!!cancelButton.isLoading}
                disabled={!!cancelButton.isLoading}
                fullWidth
              />
            )}
            {acceptButton?.label && (
              <StyledButton
                label={acceptButton.label}
                buttonTheme={ButtonTheme.PrimarySmall}
                onClick={acceptAction}
                isLoading={!!acceptButton.isLoading}
                disabled={!!acceptButton.isLoading}
                fullWidth
              />
            )}
          </ButtonsSection>
        )}
      </Container>
      <Overlay $isVisible={isVisible} />
    </SmallDialogWrapper>
  );
};
